/**
 * K-T-11 — Layer-3 hook for canonical per-vehicle-make efficiency.
 *
 * Thin wrapper around `kpi_efficiency_by_vehicle_make(shop_id,
 * start_at, end_at)`. Returns one `VehicleMakeEfficiencyResult` per
 * vehicle make with both the display totals (Book / Actual / Tickets
 * columns) and the canonical-filtered ratio + sample-gate fields.
 * Holds NO local KPI math — the RPC is the source of truth.
 *
 * Replaces the local `loadMakeStats` aggregation in
 * `MakeModelPerformance.tsx`. The component continues to handle
 * sorting / formatting / display arrangement client-side. Per-model
 * drill-down stays inline (separate aggregation shape — out of K-T-11
 * scope).
 *
 * Date-range conversion: shop-local `yyyy-MM-dd` strings → UTC ISO
 * timestamps via `fromZonedTime`, mirroring K-T-10
 * (`useJobCategoryEfficiency`) and KPI-R71-FIX.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fromZonedTime } from 'date-fns-tz';
import type { VehicleMakeEfficiencyResult } from '../../../supabase/functions/_shared/kpiCanonical';

export interface UseVehicleMakeEfficiencyOptions {
  shopId: string;
  /** Shop-local `yyyy-MM-dd`. */
  startDate: string;
  /** Shop-local `yyyy-MM-dd`. */
  endDate: string;
  shopTimezone: string;
}

export interface UseVehicleMakeEfficiencyResult {
  data: VehicleMakeEfficiencyResult[] | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Returns the canonical per-make efficiency tuple for the given shop +
 * date range. The hook converts the shop-local date strings to UTC ISO
 * timestamps before invoking the RPC.
 *
 * Stable query key:
 *   ['kpi', 'kpi_efficiency_by_vehicle_make', shopId, startDate,
 *    endDate, shopTimezone]
 */
export function useVehicleMakeEfficiency(
  opts: UseVehicleMakeEfficiencyOptions,
): UseVehicleMakeEfficiencyResult {
  const query = useQuery({
    queryKey: [
      'kpi',
      'kpi_efficiency_by_vehicle_make',
      opts.shopId,
      opts.startDate,
      opts.endDate,
      opts.shopTimezone,
    ],
    queryFn: async (): Promise<VehicleMakeEfficiencyResult[] | null> => {
      const startAt = fromZonedTime(
        `${opts.startDate}T00:00:00`,
        opts.shopTimezone,
      ).toISOString();
      const endAt = fromZonedTime(
        `${opts.endDate}T23:59:59.999`,
        opts.shopTimezone,
      ).toISOString();

      // Cast through unknown until supabase types.ts codegen catches up.
      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: { p_shop_id: string; p_start_at: string; p_end_at: string },
      ) => Promise<{
        data: VehicleMakeEfficiencyResult[] | null;
        error: unknown;
      }>)('kpi_efficiency_by_vehicle_make', {
        p_shop_id: opts.shopId,
        p_start_at: startAt,
        p_end_at: endAt,
      });

      if (error) throw error;
      return data ?? [];
    },
    enabled: Boolean(
      opts.shopId &&
        opts.startDate &&
        opts.endDate &&
        opts.shopTimezone,
    ),
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
